// One token-aware account context for the whole SPA (pricing v2, W6).
//
// Before this, five pages each fetched /auth/me/ on mount. The credits UI
// needs one shared, refreshable balance instead: every debit, purchase,
// refund, and Stripe return leg calls `refresh()` and every surface reads the
// same `credits` block. Pages that have not been migrated yet keep their own
// fetch; nothing here changes what /auth/me/ returns.
//
// Balance truthfulness (CREDITS_UI_MAP §F): a failed /auth/me/ leaves the
// balance UNKNOWN (`credits: null`, `error: true`) — never 0. The server's 409
// stays the authority on whether a spend goes through.
//
// Sellers never see credits: the provider never fetches on /shared/* or on a
// share-token touch-up gallery, so nothing account-shaped is even requested
// from a seller's browser.

import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { API_URL } from "../constants";
import { authHeader, getToken } from "../utils/auth";

/**
 * @typedef {Object} Credits
 * @property {number} balance
 * @property {string} plan_key  "free" | "agent_month" | "agent_year" | "pro_month" | "pro_year"
 * @property {string | null} pool_expires_at
 * @property {boolean} watermarked
 */

/**
 * @typedef {Object} AccountState
 * @property {any | null} account   The raw /auth/me/ payload, or null when signed out / unknown.
 * @property {Credits | null} credits  null when unknown (signed out, failed fetch, or the API flag is off).
 * @property {boolean} loading
 * @property {boolean} error       The last fetch failed — balance unknown, never 0.
 * @property {boolean} signedIn
 * @property {() => Promise<any | null>} refresh  Re-fetch /auth/me/; resolves to the payload or null.
 */

const AccountContext = createContext(/** @type {AccountState | null} */ (null));

/** @param {any} raw @returns {Credits | null} */
export const normalizeCredits = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const balance = Number(raw.balance);
  if (!Number.isFinite(balance)) return null;
  return {
    balance,
    plan_key: String(raw.plan_key || "free"),
    pool_expires_at: raw.pool_expires_at ?? null,
    watermarked: Boolean(raw.watermarked),
  };
};

/**
 * Seller-visible chrome: the public share viewer and a share-token touch-up
 * gallery. No account fetch, no balance chip, nothing credits-shaped.
 * @param {string} pathname @param {string} [search]
 */
export const isSellerRoute = (pathname, search = "") => {
  if (pathname.startsWith("/shared/")) return true;
  if (pathname.startsWith("/touchups/")) {
    try {
      return new URLSearchParams(search).has("share_token");
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * The account state machine. Used by the provider (once, for the app) and as
 * the provider-less fallback of `useAccount()` so a page rendered on its own —
 * every existing test does this — still gets the /auth/me/ fetch it had.
 *
 * @param {{ enabled: boolean, watchKey?: string }} options
 *   enabled: whether this instance may fetch at all.
 *   watchKey: re-evaluate the token on change (the provider passes the
 *   pathname — every login/logout navigates, which is how it learns).
 * @returns {AccountState}
 */
export const useAccountState = ({ enabled, watchKey = "" }) => {
  const [account, setAccount] = useState(/** @type {any | null} */ (null));
  const [credits, setCredits] = useState(/** @type {Credits | null} */ (null));
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(() => enabled && !!getToken());
  const fetchedTokenRef = useRef(/** @type {string | null} */ (null));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      if (mountedRef.current) {
        setAccount(null);
        setCredits(null);
        setError(false);
        setLoading(false);
      }
      return null;
    }
    if (mountedRef.current) setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/auth/me/`, { headers: authHeader() });
      const data = resp?.data ?? null;
      if (mountedRef.current) {
        setAccount(data);
        setCredits(normalizeCredits(data?.credits));
        setError(false);
      }
      return data;
    } catch {
      // Unknown, not zero: the chip shows "—" and the sheets skip the balance.
      if (mountedRef.current) {
        setCredits(null);
        setError(true);
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Fetch once per token. The provider re-runs this on every route change
  // (login/logout always navigate); the fallback runs it on mount only.
  // biome-ignore lint/correctness/useExhaustiveDependencies: watchKey is the deliberate re-evaluation trigger
  useEffect(() => {
    if (!enabled) return;
    const token = getToken();
    if (!token) {
      if (fetchedTokenRef.current) {
        fetchedTokenRef.current = null;
        setAccount(null);
        setCredits(null);
        setError(false);
      }
      setLoading(false);
      return;
    }
    if (fetchedTokenRef.current === token) return;
    fetchedTokenRef.current = token;
    refresh();
  }, [enabled, watchKey, refresh]);

  return useMemo(
    () => ({ account, credits, loading, error, signedIn: !!getToken(), refresh }),
    [account, credits, loading, error, refresh]
  );
};

/**
 * Mount once inside the router. Never fetches on seller routes.
 * @param {{ children: React.ReactNode }} props
 */
export const AccountProvider = ({ children }) => {
  const location = useLocation();
  const sellerRoute = isSellerRoute(location.pathname, location.search);
  const state = useAccountState({ enabled: !sellerRoute, watchKey: location.pathname });
  return <AccountContext.Provider value={state}>{children}</AccountContext.Provider>;
};

/**
 * `{ account, credits, loading, error, signedIn, refresh }`. Inside the
 * provider every consumer shares one fetch; without one (a page rendered
 * alone) the hook fetches for itself so nothing depends on the App shell.
 * @returns {AccountState}
 */
export const useAccount = () => {
  const ctx = useContext(AccountContext);
  const local = useAccountState({ enabled: !ctx });
  return ctx || local;
};

export default AccountContext;
